import React, {useCallback, useMemo, useRef, useState} from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle,
    IconButton, LinearProgress, Snackbar, Stack, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {GlInvoiceCreateProvider, useGlInvoiceCreateCtx, createStartPreload} from '@/pages/components/account/gl-invoice/create-stepper/context';
import StepACompany from '@/pages/components/account/gl-invoice/create-stepper/StepACompany';
import StepBAnnual from '@/pages/components/account/gl-invoice/create-stepper/StepBAnnual';
import StepCCateMapping from '@/pages/components/account/gl-invoice/create-stepper/StepCCateMapping';
import StepLastYear from '@/pages/components/account/gl-invoice/create-stepper/StepLastYear';
import StepCInvoiceEntry from '@/pages/components/account/gl-invoice/create-stepper/StepCInvoiceEntry';
import type {StepCInvoiceEntryHandle} from '@/pages/components/account/gl-invoice/create-stepper/StepCInvoiceEntry';
import type {GlImportResultVO} from '@/services/accountService/glInvoiceService';
import {importGlExcel} from '@/services/accountService/glInvoiceService';
import {fetchLeafGlCategories} from '@/services/accountService/glCategoryService';
import {FileUploadDialog} from '@/components/common';

interface GlCreateStepperProps {
    open: boolean;
    onCancel: () => void;
    onCreated: (message?: string) => void;
}

/** 上传时 Row 8 未匹配 → 请去 Column Mapping 手选；映射步已全部选好后不再展示 */
function isColumnMappingGlCodeWarning(warning: string): boolean {
    return /未匹配到\s*gl_code|Column Mapping/i.test(warning);
}

/** Row 4 fs-code 为空：列已进 Column Mapping 手选，不再横幅提醒「不会导入」 */
function isEmptyFsCodeImportWarning(warning: string): boolean {
    return /fs-code\s*[為为]空|不會導入|不会导入/i.test(warning);
}

function GlCreateStepperInner({onCancel, onCreated}: Omit<GlCreateStepperProps, 'open'>) {
    const {state, dispatch, crValidateRef} = useGlInvoiceCreateCtx();
    const [activeStep, setActiveStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [snackOpen, setSnackOpen] = useState(false);
    const [snackMsg, setSnackMsg] = useState('');
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState<string | null>(null);
    const [importWarnings, setImportWarnings] = useState<string[]>([]);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const stepCRef = useRef<StepCInvoiceEntryHandle>(null);
    const [crValidateMatched, setCrValidateMatched] = useState(false);
    const [crValidating, setCrValidating] = useState(false);
    const validateBtnRef = useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        setCrValidateMatched(false);
    }, [state.contentData]);

    // Select Company 起就不展示「请去 Column Mapping」类提醒（映射步自行处理未匹配列）
    React.useEffect(() => {
        if (!state.importToken && !state.companyId) return;
        setImportWarnings((prev) => {
            if (!prev.some(isColumnMappingGlCodeWarning)) return prev;
            return prev.filter((w) => !isColumnMappingGlCodeWarning(w));
        });
    }, [state.importToken, state.companyId]);

    const handleCancel = useCallback(() => {
        dispatch({type: 'clear'});
        setImportWarnings([]);
        onCancel();
    }, [dispatch, onCancel]);

    React.useEffect(() => {
        return () => {
            dispatch({type: 'clear'});
        };
    }, [dispatch]);

    // Phase 1: 上傳成功後立即後台預加載 GL 數據 + 列定義（使用共享工廠函數）
    const startPreload = useMemo(() => createStartPreload(dispatch), [dispatch]);

    // 换公司后 set_company 会把 preloadStatus 置 idle；若仍有 importToken，按新公司重新套别名
    React.useEffect(() => {
        if (!state.importToken || state.preloadStatus !== 'idle') return;
        startPreload(state.importToken, state.companyId);
    }, [state.importToken, state.companyId, state.preloadStatus, startPreload]);

    const handleImportUpload = useCallback(async (file: File) => {
        setImportError(null);
        setImportWarnings([]);
        const result = await importGlExcel(file, state.companyId ?? 0, state.annualId ?? 0);

        // 仅保留与 Select Company 相关的提醒（如未知 fs-code）；
        // 空 fs-code / Row 8 未匹配 gl_code 留给 Column Mapping 手选，不在横幅刷屏
        const warnings = (result.fsCodeWarnings ?? []).filter(
            (w) => !isColumnMappingGlCodeWarning(w) && !isEmptyFsCodeImportWarning(w),
        );
        if (warnings.length > 0) {
            setImportWarnings(warnings);
        }

        // Store Company Info 解析数据
        if (result.companyInfoData) {
            dispatch({type: 'set_content_data', payload: result.companyInfoData});
        }

        // Store import flags
        dispatch({
            type: 'set_import_flags', payload: {
                companyMatched: result.companyMatched ?? false,
                companyNameUpdated: result.companyNameUpdated ?? false,
            }
        });

        // ★ 必须先设 importToken，再 set_company：
        // set_company 在无 importToken 时会清空 unmatchedColumns / contentData，
        // 否则会吞掉 Column Mapping 步骤（只剩 Last Year → Enter Invoices）。
        if (result.importToken) {
            dispatch({type: 'set_import_token', payload: result.importToken});
        }
        if (result.legacyFormat) {
            dispatch({type: 'set_legacy_format', payload: true});
        }
        // Always-On Column Mapping：有 token 即进入映射步（全量列，含已匹配预填）
        const mappings = (result.columnMappings?.length
            ? result.columnMappings
            : result.unmatchedColumns) ?? [];
        if (result.importToken && mappings.length) {
            dispatch({type: 'set_column_mappings', payload: mappings});
            dispatch({type: 'set_unmatched_columns', payload: mappings});
            dispatch({type: 'set_pending_legacy_mapping', payload: true});
            fetchLeafGlCategories()
                .then(cats => dispatch({type: 'set_preloaded_gl_categories', payload: cats}))
                .catch(e => console.warn('Pre-fetch GL categories failed', e));
        }

        const matchedCompanyId = result.matchedCompany
            ? ((result.matchedCompany as any).companyId ?? (result.matchedCompany as any).id ?? null)
            : null;

        if (result.matchedCompany) {
            dispatch({type: 'set_imported_company', payload: result.matchedCompany});
            dispatch({type: 'set_company', payload: result.matchedCompany});
        }

        if (result.importToken) {
            // unmatched 行带 _UNM_ 占位 key，mapping 确认后再客户端重映射
            startPreload(result.importToken, matchedCompanyId ?? state.companyId);
        }

        return result;
    }, [state.companyId, state.annualId, dispatch, startPreload]);

    // 支持 inlineCreate 模式：beforeNext 存在时允许点击 Next（由 StepACompany 注册）
    const canNextStep0 = !!state.selectedCompany || !!state.beforeNext;
    const canNextStep1 = (!!state.annualId && !state.isCreatingAnnual) || !!state.beforeNext;

    // 动态步骤标签：有 importToken + 映射列即插入 Column Mapping（Always-On）
    const stepLabels = useMemo(() => {
        const base = ['Select Company', 'Select Annual'];
        const hasMapping = !!(state.importToken && (
            state.columnMappings?.length || state.unmatchedColumns?.length
        ));
        if (hasMapping) {
            base.push('Column Mapping');
        }
        base.push('Last Year', 'Enter Invoices');
        return base;
    }, [state.importToken, state.columnMappings, state.unmatchedColumns]);

    const totalSteps = stepLabels.length;
    const invoiceStepIndex = totalSteps - 1;
    const lastYearStepIndex = totalSteps - 2;
    const mappingStepIndex = stepLabels.indexOf('Column Mapping');

    const linearValue = useMemo(() => ((activeStep + 1) / stepLabels.length) * 100, [activeStep, stepLabels]);

    const handleNext = useCallback(async () => {
        dispatch({type: 'set_step_error', payload: null});
        if (state.beforeNext) {
            dispatch({type: 'set_is_processing', payload: true});
            try {
                const ok = await state.beforeNext();
                if (!ok) return;
            } finally {
                dispatch({type: 'set_is_processing', payload: false});
            }
        }
        setActiveStep((s) => Math.min(stepLabels.length - 1, s + 1));
    }, [state.beforeNext, dispatch, stepLabels]);

    const handleBack = useCallback(() => {
        setSaveError(null);
        dispatch({type: 'set_step_error', payload: null});
        setActiveStep((s) => Math.max(0, s - 1));
    }, [dispatch]);

    const handleSaveSuccess = useCallback(async (result: GlImportResultVO) => {
        const msg = `Saved — ${result.totalRows ?? 0} rows, ${result.insertedCount ?? 0} new, ${result.updatedCount ?? 0} updated${result.errorCount > 0 ? `, ${result.errorCount} errors` : ''}`;
        setSnackMsg(msg);
        setSnackOpen(true);
        dispatch({type: 'clear'});
        onCreated(msg);
    }, [dispatch, onCreated]);

    const handleSaveError = useCallback((msg: string) => {
        setSaveError(msg);
    }, []);

    const nextDisabled = useMemo(() => {
        if (state.isProcessing) return true;
        if (activeStep === 0) return !canNextStep0;
        if (activeStep === 1) return !canNextStep1;
        return false;
    }, [activeStep, canNextStep0, canNextStep1, state.isProcessing]);

    return (
        <Box sx={{width: '100%', display: 'flex', flexDirection: 'column', height: '100%'}}>
            <Box sx={{px: 2, mb: 1}}>
                <Stack spacing={0.5}>
                    <LinearProgress variant="determinate" value={linearValue}/>
                    <Stack direction="row" justifyContent="space-between">
                        {stepLabels.map((label: string, i: number) => (
                            <Typography
                                key={label}
                                variant="caption"
                                color={activeStep >= i ? 'text.primary' : 'text.disabled'}
                            >
                                {label}
                            </Typography>
                        ))}
                    </Stack>
                </Stack>
            </Box>

            {/* Import Excel upload button — hidden after successful upload */}
            <Box sx={{px: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1}}>
                {!state.contentData && (
                    <Tooltip title="Import GL Excel (.xlsm) to auto-fill company info">
                        <span>
                            <IconButton
                                size="small"
                                color="primary"
                                disabled={importing}
                                onClick={() => setImportDialogOpen(true)}
                            >
                                {importing ? <CircularProgress size={18}/> : <UploadFileIcon/>}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {state.contentData && (
                    <Typography variant="caption" color="success.main">Company info imported ✓</Typography>
                )}
                {importError && (
                    <Typography variant="caption" color="error">{importError}</Typography>
                )}
            </Box>

            {importWarnings.length > 0 && (
                <Box sx={{px: 2, mb: 1}}>
                    <Alert severity="warning" onClose={() => setImportWarnings([])}>
                        <Typography variant="subtitle2" sx={{mb: 0.5}}>
                            GL 导入提醒
                        </Typography>
                        <Box component="ul" sx={{m: 0, pl: 2.5}}>
                            {importWarnings.map((warn, idx) => (
                                <Typography key={`${idx}-${warn}`} component="li" variant="body2">
                                    {warn}
                                </Typography>
                            ))}
                        </Box>
                    </Alert>
                </Box>
            )}

            <FileUploadDialog
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
                onSuccess={() => {
                    setImportDialogOpen(false);
                }}
                onUploadingChange={setImporting}
                onUpload={handleImportUpload}
                title="Import GL Excel"
                accept=".xlsm"
                acceptLabel="Accepted: .xlsm only · Max size: 10MB"
            />

            {saveError && (
                <Box sx={{px: 2, mb: 1}}>
                    <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>
                </Box>
            )}

            {state.stepError && (
                <Box sx={{px: 2, mb: 1}}>
                    <Alert
                        severity="error"
                        onClose={() => dispatch({ type: 'set_step_error', payload: null })}
                    >
                        {state.stepError}
                    </Alert>
                </Box>
            )}

            <Box sx={{px: 2, flex: 1, overflowY: 'auto'}}>
                {activeStep === 0 && <StepACompany/>}
                {activeStep === 1 && <StepBAnnual/>}
                {mappingStepIndex >= 0 && activeStep === mappingStepIndex && (
                    <StepCCateMapping/>
                )}
                {activeStep === lastYearStepIndex && <StepLastYear/>}
                {activeStep === invoiceStepIndex && state.companyId && state.annualId && (
                    <StepCInvoiceEntry
                        ref={stepCRef}
                        companyId={state.companyId}
                        annualId={state.annualId}
                        importToken={state.importToken}
                        onSaveSuccess={handleSaveSuccess}
                        onSaveError={handleSaveError}
                        saving={saving}
                        onSavingChange={setSaving}
                    />
                )}
            </Box>

            <Stack
                direction="row"
                spacing={1}
                justifyContent="flex-end"
                sx={{px: 2, pt: 2, mt: 2, borderTop: 1, borderColor: 'divider'}}
            >
                <Button onClick={handleCancel} disabled={saving}>Cancel</Button>
                <Box flex={1}/>
                <Button
                    disabled={activeStep === 0 || saving || state.isProcessing}
                    onClick={handleBack}
                >
                    Back
                </Button>
                {activeStep === invoiceStepIndex && (
                    <Button
                        variant="contained"
                        color="success"
                        disabled={saving}
                        onClick={() => stepCRef.current?.triggerSave()}
                    >
                        {saving ? 'Saving...' : 'Confirm'}
                    </Button>
                )}
                {activeStep === 0 && !!state.contentData && (
                    crValidateMatched ? (
                        <Alert
                            severity="success"
                            icon={<CheckCircleOutlineIcon fontSize="inherit"/>}
                            sx={{py: 0, alignItems: 'center'}}
                        >
                            Validate result: matched
                        </Alert>
                    ) : (
                        <Button
                            ref={validateBtnRef}
                            variant="outlined"
                            disabled={crValidating || saving || state.isProcessing}
                            onClick={async () => {
                                setCrValidating(true);
                                try {
                                    const outcome = await crValidateRef.current?.validate(validateBtnRef.current) ?? 'error';
                                    if (outcome === 'matched') setCrValidateMatched(true);
                                } finally {
                                    setCrValidating(false);
                                }
                            }}
                        >
                            {crValidating ? 'Validating...' : 'Validate'}
                        </Button>
                    )
                )}
                {activeStep < stepLabels.length - 1 && (
                    <Button
                        variant="contained"
                        disabled={nextDisabled}
                        onClick={handleNext}
                    >
                        {state.isProcessing ? 'Processing...' : 'Next'}
                    </Button>
                )}
            </Stack>

            <Snackbar
                open={snackOpen}
                autoHideDuration={3000}
                onClose={() => setSnackOpen(false)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert onClose={() => setSnackOpen(false)} severity="success" variant="filled" sx={{width: '100%'}}>
                    {snackMsg}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export function GlCreateStepper({open, onCancel, onCreated}: GlCreateStepperProps) {
    const [fullScreen, setFullScreen] = useState(false);

    React.useEffect(() => {
        if (!open) setFullScreen(false);
    }, [open]);

    if (!open) return null;
    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason === 'backdropClick') return;
                onCancel();
            }}
            disableEscapeKeyDown
            fullScreen={fullScreen}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: fullScreen
                    ? {height: '100%', maxHeight: '100%', m: 0, borderRadius: 0}
                    : {width: '95vw', maxWidth: 1600, height: '85vh'},
            }}
        >
            <DialogTitle sx={{display: 'flex', alignItems: 'center', pb: 1}}>
                Create General Ledger
                <Stack direction="row" spacing={0.5} sx={{ml: 'auto'}} alignItems="center">
                    <Tooltip title={fullScreen ? 'Exit full screen' : 'Full screen'}>
                        <IconButton size="small" onClick={() => setFullScreen((v) => !v)}>
                            {fullScreen ? <FullscreenExitIcon fontSize="small"/> : <FullscreenIcon fontSize="small"/>}
                        </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={onCancel}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{p: 2, display: 'flex', overflow: 'hidden', minHeight: 0}}>
                <Box sx={{flex: 1, display: 'flex', minHeight: 0, minWidth: 0}}>
                    <GlInvoiceCreateProvider>
                        <GlCreateStepperInner onCancel={onCancel} onCreated={onCreated}/>
                    </GlInvoiceCreateProvider>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
